import { useCallback, useEffect, useState } from 'react';

export interface ManagedListApi<T, D> {
  list: () => Promise<T[]>;
  create: (draft: D) => Promise<unknown>;
  update: (id: string, draft: D) => Promise<unknown>;
  remove: (id: string) => Promise<void>;
}

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/**
 * State for a Manage page: the list, the add/edit modal (`editing`: an item, 'new', or null),
 * the delete confirmation (`deleting`), and the save/delete actions.
 * Load errors go in `error` (shown on the page); save/delete errors go in `modalError` (shown in the open modal).
 * After every change the list is reloaded from the server (it decides the order) and `onChanged` runs.
 * `api` must be a stable object (define it at module level).
 */
export function useManagedList<T extends { id: string }, D>(api: ManagedListApi<T, D>, onChanged?: () => void) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<T | 'new' | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    try {
      setItems(await api.list());
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Runs a change; on success reloads and closes the modal, on failure keeps it open with the error. */
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setModalError(null);
    try {
      await fn();
      setEditing(null);
      setDeleting(null);
      await reload();
      onChanged?.();
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const open = (edit: T | 'new' | null, del: T | null) => {
    setModalError(null);
    setEditing(edit);
    setDeleting(del);
  };

  return {
    items,
    loading,
    error,
    dismissError: () => setError(null),
    editing,
    deleting,
    modalError,
    busy,
    startCreate: () => open('new', null),
    startEdit: (item: T) => open(item, null),
    startDelete: (item: T) => open(null, item),
    close: () => {
      if (!busy) open(null, null);
    },
    save: (draft: D) => {
      const target = editing;
      if (!target) return;
      void run(() => (target === 'new' ? api.create(draft) : api.update(target.id, draft)));
    },
    confirmDelete: () => {
      const target = deleting;
      if (target) void run(() => api.remove(target.id));
    },
  };
}
